import type { FormInstance } from "antd"

import { CUSTOM } from "./duration-presets"
import type { AutomationFormValues } from "./form-values"

type FieldName = keyof AutomationFormValues

// The builder's fields in on-screen order, so "first" means first on the page
// rather than first to register (a revealed field registers last).
const FIELD_ORDER: FieldName[] = [
  "name",
  "channel",
  "category",
  "lead_status",
  "delay_preset",
  "delay_weeks",
  "delay_days",
  "delay_hours",
  "delay_minutes",
  "repeat",
  "send_count",
  "cadence_preset",
  "cadence_weeks",
  "cadence_days",
  "cadence_hours",
  "source_template",
  "subject",
  "body",
  "sender_user"
]

type ErrorField = { name: (string | number)[] }

export const firstInvalidField = (
  errorFields: ErrorField[]
): FieldName | undefined => {
  const invalid = new Set(errorFields.map((field) => field.name[0]))
  return FIELD_ORDER.find((name) => invalid.has(name))
}

/** Puts the caret in the rich-text body, which AntD cannot focus for us. */
export type FocusBody = () => void

// Scrolls a field into view and focuses it. AntD's own focus does not reach
// the rich-text editor behind `body` (it sits in a noStyle item under a
// wrapper), so that one is focused through the editor's handle.
export const focusField = (
  form: FormInstance<AutomationFormValues>,
  name: FieldName,
  focusBody?: FocusBody
) => {
  if (name === "body" && focusBody) {
    form.scrollToField(name, { block: "center" })
    focusBody()
    return
  }
  form.scrollToField(name, { block: "center", focus: true })
}

export const focusFirstInvalid = (
  form: FormInstance<AutomationFormValues>,
  errorFields: ErrorField[],
  focusBody?: FocusBody
) => {
  const name = firstInvalidField(errorFields)
  if (name) focusField(form, name, focusBody)
}

// Runs the form's own rules; on failure focuses the first invalid field.
export const validateBuilder = async (
  form: FormInstance<AutomationFormValues>,
  focusBody?: FocusBody
) => {
  try {
    await form.validateFields()
    return true
  } catch (error) {
    focusFirstInvalid(
      form,
      (error as { errorFields?: ErrorField[] }).errorFields ?? [],
      focusBody
    )
    return false
  }
}

// Whether a field is on screen for these values: the lead state hides for a
// Nurturing automation, the repeat fields behind "Send more than once", the
// exact parts behind "Custom".
export const isFieldShown = (
  name: FieldName,
  values: AutomationFormValues
): boolean => {
  switch (name) {
    case "lead_status":
      return values.category !== "NURTURING"
    case "delay_weeks":
    case "delay_days":
    case "delay_hours":
    case "delay_minutes":
      return values.delay_preset === CUSTOM
    case "send_count":
    case "cadence_preset":
      return values.repeat
    case "cadence_weeks":
    case "cadence_days":
    case "cadence_hours":
      return values.repeat && values.cadence_preset === CUSTOM
    default:
      return true
  }
}
